import type { ReactNode } from "react";

import { SelectField, TextAreaField, TextField } from "../../../ui/fields";
import type { SubtypeValues } from "../../form-mappers";
import type { Option } from "../../labels";
import type { FieldErrors } from "../../validation";

/** What every type's own section receives. A new item type adds one component that takes these props. */
export type SubtypeSectionProps = Readonly<{
  idPrefix: string;
  values: SubtypeValues;
  errors: FieldErrors;
  disabled: boolean;
  onChange: (key: string, value: string) => void;
}>;

type Extra = Readonly<{ hint?: ReactNode; maxLength?: number; required?: boolean }>;

/** Small builders for the controls of a type section: they wire the id, value, error and change handler. */
export function subtypeControls({
  idPrefix,
  values,
  errors,
  disabled,
  onChange,
}: SubtypeSectionProps) {
  const id = (key: string) => `${idPrefix}-${key}`;
  const value = (key: string) => values[key] ?? "";
  return {
    text: (key: string, label: string, extra: Extra = {}) => (
      <TextField
        key={key}
        id={id(key)}
        label={label}
        value={value(key)}
        onChange={(next) => onChange(key, next)}
        error={errors[key]}
        disabled={disabled}
        {...extra}
      />
    ),
    whole: (key: string, label: string, extra: Extra = {}) => (
      <TextField
        key={key}
        id={id(key)}
        label={label}
        type="number"
        inputMode="numeric"
        value={value(key)}
        onChange={(next) => onChange(key, next)}
        error={errors[key]}
        disabled={disabled}
        {...extra}
      />
    ),
    time: (key: string, label: string, extra: Extra = {}) => (
      <TextField
        key={key}
        id={id(key)}
        label={label}
        type="time"
        value={value(key)}
        onChange={(next) => onChange(key, next)}
        error={errors[key]}
        disabled={disabled}
        {...extra}
      />
    ),
    dateTime: (key: string, label: string, extra: Extra = {}) => (
      <TextField
        key={key}
        id={id(key)}
        label={label}
        type="datetime-local"
        value={value(key)}
        onChange={(next) => onChange(key, next)}
        error={errors[key]}
        disabled={disabled}
        {...extra}
      />
    ),
    area: (key: string, label: string, extra: Extra & { rows?: number } = {}) => (
      <div className="col-span-full" key={key}>
        <TextAreaField
          id={id(key)}
          label={label}
          value={value(key)}
          onChange={(next) => onChange(key, next)}
          error={errors[key]}
          disabled={disabled}
          {...extra}
        />
      </div>
    ),
    select: (
      key: string,
      label: string,
      options: readonly Option[],
      placeholder?: string,
      extra: Extra = {},
    ) => (
      <SelectField
        key={key}
        id={id(key)}
        label={label}
        value={value(key)}
        onChange={(next) => onChange(key, next)}
        options={options}
        {...(placeholder === undefined ? {} : { placeholder })}
        error={errors[key]}
        disabled={disabled}
        {...extra}
      />
    ),
  };
}
